import React, { useState, useEffect } from 'react';
import {
  Search,
  Volume2,
  HelpCircle,
  CheckCircle2,
  XCircle,
  FileText,
  Lightbulb
} from 'lucide-react';
import { SessionGameRound, SessionPlayerState } from '../../../types/session-games';
import { useSpeechSynthesis } from '../../../hooks/useSpeechSynthesis';
import { playCorrectSound, playWrongSound } from '../../../utils/soundEffects';

interface NewsDetectiveViewProps {
  round: SessionGameRound;
  myPlayer: SessionPlayerState;
  partnerPlayer?: SessionPlayerState;
  onSubmit: (result: {
    answer: string;
    isCorrect: boolean;
    points: number;
  }) => void;
  isRoundResult: boolean;
}

export const NewsDetectiveView: React.FC<NewsDetectiveViewProps> = ({
  round,
  myPlayer,
  partnerPlayer,
  onSubmit,
  isRoundResult
}) => {
  const { speak } = useSpeechSynthesis({ lang: 'en-US' });
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [revealedClues, setRevealedClues] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setSelectedWord(null);
    setSubmitted(false);
    setRevealedClues({});
  }, [round.id]);

  const handleSelect = (word: string) => {
    if (submitted || isRoundResult) return;
    setSelectedWord(word);
    setSubmitted(true);

    const isCorrect = word.toLowerCase() === round.correctAnswer.toLowerCase();
    const points = isCorrect ? 15 : 0;

    if (isCorrect) {
      playCorrectSound();
    } else {
      playWrongSound();
    }

    onSubmit({
      answer: word,
      isCorrect,
      points
    });
  };

  const toggleClue = (key: string) => {
    setRevealedClues((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const options = round.options || [round.correctAnswer, 'solution', 'process', 'impact'];

  return (
    <div className="flex flex-col items-center justify-between h-full max-w-2xl mx-auto space-y-6">
      {/* News Article Excerpt */}
      <div className="w-full bg-white rounded-3xl p-6 md:p-8 border border-purple-100 shadow-md">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4">
          <div className="flex items-center gap-1.5 text-xs font-bold text-purple-600 uppercase tracking-wider">
            <Search className="w-4 h-4" />
            <span>News Detective • Fill the Cloze</span>
          </div>

          <span className="text-[11px] bg-purple-50 text-purple-700 px-2.5 py-0.5 rounded-full font-bold">
            Live News Excerpt
          </span>
        </div>

        <p className="text-base md:text-xl font-medium text-gray-800 leading-relaxed">
          {round.prompt.replace(/^Find the missing word:\s*"?/, '').replace(/"?$/, '')}
        </p>
      </div>

      {/* Detective Clues Drawer */}
      <div className="w-full bg-purple-50/60 rounded-2xl p-4 border border-purple-200/70 space-y-2">
        <div className="flex items-center gap-1 text-xs font-bold text-purple-900 uppercase tracking-wider mb-1">
          <Lightbulb className="w-4 h-4 text-amber-500" />
          <span>Detective Clues:</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {round.clues?.definition && (
            <button
              onClick={() => toggleClue('def')}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                revealedClues['def']
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-purple-700 border border-purple-200'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              <span>{revealedClues['def'] ? 'Hide Definition' : 'Show Definition'}</span>
            </button>
          )}

          {round.clues?.audioClue && (
            <button
              onClick={() => {
                toggleClue('audio');
                speak(round.clues!.audioClue!, 'en-US');
              }}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-white text-purple-700 border border-purple-200 hover:bg-purple-100 flex items-center gap-1.5 transition-all"
            >
              <Volume2 className="w-3.5 h-3.5" />
              <span>Listen Word Audio</span>
            </button>
          )}

          {round.clues?.translationClue && (
            <button
              onClick={() => toggleClue('trans')}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                revealedClues['trans']
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-purple-700 border border-purple-200'
              }`}
            >
              <HelpCircle className="w-3.5 h-3.5" />
              <span>Hint</span>
            </button>
          )}
        </div>

        {revealedClues['def'] && round.clues?.definition && (
          <p className="text-xs text-purple-900 bg-white p-3 rounded-xl border border-purple-100 mt-2 font-medium">
            💡 <strong>Definition:</strong> {round.clues.definition}
          </p>
        )}

        {revealedClues['trans'] && round.clues?.translationClue && (
          <p className="text-xs text-purple-900 bg-white p-3 rounded-xl border border-purple-100 mt-2 font-medium">
            💡 <strong>Hint:</strong> {round.clues.translationClue}
          </p>
        )}
      </div>

      {/* Word Choices Grid */}
      <div className="w-full grid grid-cols-2 gap-3">
        {options.map((opt, idx) => {
          const isChosen = selectedWord === opt;
          const isCorrect = opt.toLowerCase() === round.correctAnswer.toLowerCase();

          let btnClass = 'bg-white hover:bg-purple-50 text-gray-800 border-gray-200';
          if (isRoundResult || submitted) {
            if (isCorrect) {
              btnClass = 'bg-emerald-500 text-white border-emerald-600 shadow-md';
            } else if (isChosen && !isCorrect) {
              btnClass = 'bg-red-500 text-white border-red-600';
            } else {
              btnClass = 'bg-gray-50 text-gray-400 border-gray-200 opacity-60';
            }
          }

          return (
            <button
              key={`${opt}_${idx}`}
              onClick={() => handleSelect(opt)}
              disabled={submitted || isRoundResult}
              className={`p-3.5 rounded-2xl border-2 font-bold text-sm md:text-base transition-all transform active:scale-95 flex items-center justify-between shadow-xs ${btnClass}`}
            >
              <span>{opt}</span>
              {(isRoundResult || submitted) && isCorrect && <CheckCircle2 className="w-4 h-4 text-white" />}
              {(isRoundResult || submitted) && isChosen && !isCorrect && <XCircle className="w-4 h-4 text-white" />}
            </button>
          );
        })}
      </div>

      {/* Partner Status Card */}
      {partnerPlayer && (
        <div className="w-full bg-white rounded-2xl p-3 border border-gray-100 shadow-xs flex items-center justify-between text-xs">
          <span className="font-bold text-gray-800">{partnerPlayer.userName}:</span>
          <span className={partnerPlayer.roundFinished ? 'text-emerald-600 font-bold' : 'text-gray-400'}>
            {partnerPlayer.roundFinished ? '✓ Investigated!' : 'Reading excerpt...'}
          </span>
        </div>
      )}
    </div>
  );
};
